import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Alert } from '@mui/material';
import {} from '@diggens/shared-hooks';
import { authenticatedFetch } from '@diggens/api-client';
import { useCurrentUser } from '@diggens/user-management';
import { useQueryClient } from '@tanstack/react-query';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  portfolioCompanyId: string;
  portfolioCompanyName: string;
  tenantId: string;
}

export function InviteUserDialog({ open, onClose, portfolioCompanyId, portfolioCompanyName, tenantId }: InviteUserDialogProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !firstName || !lastName || !currentUser) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const redirectTo = `${window.location.origin}/auth/set-password`;

      const response = await authenticatedFetch('/users/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          title: title.trim() || undefined,
          tenant_id: tenantId,
          portfolio_company_id: portfolioCompanyId,
          invited_by: currentUser.id,
          role: 'portco',
          redirect_to: redirectTo,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to invite user');
      }

      // Invalidate users queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['users'] });

      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');
      setTitle('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setTitle('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Invite User to {portfolioCompanyName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}

            <TextField autoFocus label="First Name" fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" required />

            <TextField label="Last Name" fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" required />

            <TextField label="Email Address" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" required />

            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="CFO, Controller, VP Finance, etc."
              helperText="Optional - User's job title at their company"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || !email || !firstName || !lastName}>
            {isLoading ? 'Inviting...' : 'Invite User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
